import { IconArrowsCheck } from '@doltech/icons/IconArrowsCheck';
import cl from 'classnames';
import { noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useSlateStatic } from 'slate-react';
import styled from 'styled-components';

import { colorsV2 } from '../../../../../colors-v2';
import { Typography } from '../../../../../DesignSystemV2/Typography';
import { Separator } from '../../../../../Layout/Landing/Content/Separator';
import { useCardLogicStore } from '../../model/card-logic.store';
import { CardLogicTransformActions } from '../../model/transforms';

const MENU_MIN_WIDTH = 214;

export const MenuMain = styled.div`
  background-color: ${colorsV2.white100};
  .menu {
    min-width: ${MENU_MIN_WIDTH}px;
    padding: 8px 0;
  }
`;

const MenuItemMain = styled.div`
  .menu-item {
    display: grid;
    grid-template-columns: 16px minmax(0, 1fr);
    gap: 12px;
    align-items: center;
    transition: all 0.2s ease;
    cursor: pointer;
    padding: 12px;
    &:hover {
      background-color: ${colorsV2.primary5};
      color: ${colorsV2.primary100};
    }
    &-icon {
      opacity: 0;
      font-size: 16px;
      line-height: 0;
    }
    &-text {
      pointer-events: none;
      user-select: none;
    }
  }

  &.active {
    .menu-item {
      &-icon {
        opacity: 1;
      }
    }
  }

  &.disabled {
    .menu-item {
      cursor: not-allowed;
      color: ${colorsV2.gray40};
    }
  }
`;

interface MenuItemProps extends React.HTMLAttributes<HTMLDivElement> {
  isActive?: boolean;
  disabled?: boolean;
}

export const MenuItem = ({
  children,
  className,
  isActive,
  disabled,
  onMouseDown = noop,
  ...rest
}: MenuItemProps) => {
  return (
    <MenuItemMain
      className={cl(className, 'menu-item-wrapper', { active: isActive, disabled })}
      onMouseDown={(e) => {
        e.preventDefault();
        if (disabled) return;
        onMouseDown(e);
      }}
      {...rest}
    >
      <div className="menu-item">
        <div className="menu-item-icon">
          <IconArrowsCheck />
        </div>
        <div className="menu-item-text">
          <Typography.Text variant="medium/14">{children}</Typography.Text>
        </div>
      </div>
    </MenuItemMain>
  );
};

interface CardLogicHeaderPopoverMenuProps {
  element: any;
}

export const CardLogicHeaderPopoverMenu = observer(
  ({ element }: CardLogicHeaderPopoverMenuProps) => {
    const editor = useSlateStatic();
    const cardLogicStore = useCardLogicStore();

    return (
      <MenuMain className="header-menu">
        <div className="menu">
          <MenuItem
            isActive={element.h3Enabled}
            onMouseDown={() => {
              CardLogicTransformActions.setNodesByElementId({
                editor,
                value: { h3Enabled: !element.h3Enabled },
                elementId: element.id,
              });
            }}
          >
            Gán thẻ H3
          </MenuItem>
          <MenuItem
            isActive={element.backgroundColor !== 'transparent'}
            onMouseDown={() => {
              CardLogicTransformActions.setNodesByElementId({
                editor,
                value: {
                  backgroundColor:
                    element.backgroundColor === 'transparent' ? colorsV2.yellow40 : 'transparent',
                },
                elementId: element.id,
              });
            }}
          >
            Background Color
          </MenuItem>
          <Separator backgroundColor="black10" variant="horizontal" />

          <MenuItem
            isActive={element.textAlign === 'left'}
            onMouseDown={() => {
              CardLogicTransformActions.setNodesByElementId({
                editor,
                value: {
                  textAlign: 'left',
                },
                elementId: element.id,
              });
            }}
          >
            Align Left
          </MenuItem>
          <MenuItem
            isActive={element.textAlign === 'center'}
            onMouseDown={() => {
              CardLogicTransformActions.setNodesByElementId({
                editor,
                value: {
                  textAlign: 'center',
                },
                elementId: element.id,
              });
            }}
          >
            Align Middle
          </MenuItem>
          <MenuItem
            isActive={element.textAlign === 'right'}
            onMouseDown={() => {
              CardLogicTransformActions.setNodesByElementId({
                editor,
                value: {
                  textAlign: 'right',
                },
                elementId: element.id,
              });
            }}
          >
            Align Right
          </MenuItem>

          <Separator backgroundColor="black10" variant="horizontal" />
          <MenuItem
            onMouseDown={() => {
              cardLogicStore.insertColumnNext({
                editor,
                elementId: element.id,
              });
            }}
          >
            Insert Column Next
          </MenuItem>
          <MenuItem
            disabled={cardLogicStore.columnCount <= 2}
            onMouseDown={() => {
              cardLogicStore.removeColumn({
                editor,
                elementId: element.id,
              });
            }}
          >
            Remove Column
          </MenuItem>
        </div>
      </MenuMain>
    );
  }
);
